import { LOCALES, type ArbDocument, type JsonObject, type JsonValue, type Locale, type ValidationIssue } from "./types";

export const ARB_FILENAMES: Record<Locale, string> = {
  de: "app_de.arb",
  en: "app_en.arb",
  fr: "app_fr.arb",
  it: "app_it.arb",
};

const PLACEHOLDER_PATTERN = /\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*(?:[,}])/g;
const LEGACY_EMPTY_METADATA = { placeholders: { brand: {} } };

export interface NormalizedArbResult {
  document: ArbDocument;
  contexts: Record<string, string>;
  changed: boolean;
  messages: string[];
}

function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseArb(filename: string, text: string, expectedLocale: Locale): ArbDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new Error(`${filename} is not valid JSON: ${detail}`);
  }

  if (!isJsonObject(parsed)) {
    throw new Error(`${filename} must contain one JSON object.`);
  }

  if (parsed["@@locale"] !== expectedLocale) {
    throw new Error(`${filename} must declare "@@locale": "${expectedLocale}".`);
  }

  for (const [key, value] of Object.entries(parsed)) {
    if (key === "@@locale" || key.startsWith("@")) continue;
    if (!key) throw new Error(`${filename} contains an empty translation key.`);
    if (key.endsWith("@@context")) {
      throw new Error(`${filename} contains legacy context key "${key}". Move it to a standard @key metadata object.`);
    }
    if (typeof value !== "string") {
      throw new Error(`${filename} key "${key}" must contain a string.`);
    }
  }

  return { locale: expectedLocale, filename, data: parsed, originalText: text };
}

export function parseArbWithLegacyNormalization(filename: string, text: string, expectedLocale: Locale): NormalizedArbResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Delegate to the strict parser for its detailed error message.
    return { document: parseArb(filename, text, expectedLocale), contexts: {}, changed: false, messages: [] };
  }
  if (!isJsonObject(parsed)) {
    return { document: parseArb(filename, text, expectedLocale), contexts: {}, changed: false, messages: [] };
  }

  const contexts: Record<string, string> = {};
  const messages: string[] = [];
  let changed = false;

  for (const key of Object.keys(parsed)) {
    if (!key.endsWith("@@context")) continue;
    const translationKey = key.slice(0, -"@@context".length);
    const context = parsed[key];
    if (!translationKey || typeof parsed[translationKey] !== "string" || typeof context !== "string") {
      throw new Error(`${filename} contains invalid legacy context key "${key}".`);
    }
    contexts[translationKey] = context;
    delete parsed[key];
    changed = true;
    messages.push(`Moved ${key} to standard @${translationKey} metadata in app_en.arb.`);
  }

  if (Object.hasOwn(parsed, "")) {
    const malformed = parsed[""];
    if (
      JSON.stringify(malformed) !== JSON.stringify(LEGACY_EMPTY_METADATA)
      || typeof parsed.campaignRegisterCardTitle !== "string"
      || !parsed.campaignRegisterCardTitle.includes("{brand}")
    ) {
      throw new Error(`${filename} contains an unknown empty-key object and cannot be normalized safely.`);
    }
    delete parsed[""];
    changed = true;
    messages.push("Removed the verified misplaced campaignRegisterCardTitle placeholder object.");
  }

  const normalizedText = `${JSON.stringify(parsed, null, 2)}\n`;
  const document = parseArb(filename, normalizedText, expectedLocale);
  document.originalText = text;
  return { document, contexts, changed, messages };
}

export function getTranslationKeys(document: ArbDocument): string[] {
  return Object.entries(document.data)
    .filter(([key, value]) => key !== "@@locale" && !key.startsWith("@") && typeof value === "string")
    .map(([key]) => key);
}

export function getTranslation(document: ArbDocument, key: string): string {
  const value = document.data[key];
  if (typeof value !== "string") throw new Error(`${document.filename} does not contain string key "${key}".`);
  return value;
}

export function setTranslation(document: ArbDocument, key: string, value: string): void {
  if (typeof document.data[key] !== "string") {
    throw new Error(`Cannot edit unknown translation key "${key}".`);
  }
  document.data[key] = value;
}

export function serializeArb(document: ArbDocument): string {
  return `${JSON.stringify(document.data, null, 2)}\n`;
}

export function extractPlaceholders(value: string): string[] {
  return [...new Set([...value.matchAll(PLACEHOLDER_PATTERN)].map((match) => match[1]).filter(Boolean) as string[])].sort();
}

export function validateArbSet(arbs: Record<Locale, ArbDocument>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const canonicalKeys = getTranslationKeys(arbs.en);
  const canonicalSet = new Set(canonicalKeys);

  for (const locale of LOCALES) {
    const keys = getTranslationKeys(arbs[locale]);
    const set = new Set(keys);
    const missing = canonicalKeys.filter((key) => !set.has(key));
    const extra = keys.filter((key) => !canonicalSet.has(key));
    if (missing.length) {
      issues.push({
        level: "error",
        code: "missing-keys",
        locale,
        message: `${locale.toUpperCase()} is missing ${missing.length} key(s): ${missing.slice(0, 5).join(", ")}`,
      });
    }
    if (extra.length) {
      issues.push({
        level: "error",
        code: "extra-keys",
        locale,
        message: `${locale.toUpperCase()} has ${extra.length} extra key(s): ${extra.slice(0, 5).join(", ")}`,
      });
    }
  }

  if (issues.some((issue) => issue.level === "error")) return issues;

  for (const key of canonicalKeys) {
    const placeholderSets = LOCALES.map((locale) => ({
      locale,
      placeholders: extractPlaceholders(getTranslation(arbs[locale], key)),
    }));
    const expected = placeholderSets[0]?.placeholders.join("|") ?? "";
    for (const current of placeholderSets.slice(1)) {
      if (current.placeholders.join("|") !== expected) {
        issues.push({
          level: "error",
          code: "placeholder-mismatch",
          key,
          locale: current.locale,
          message: `${key}: placeholder set differs in ${current.locale.toUpperCase()}.`,
        });
      }
    }
  }

  return issues;
}

export function cloneArb(document: ArbDocument): ArbDocument {
  return {
    ...document,
    data: structuredClone(document.data) as JsonObject,
  };
}

export function isJsonValue(value: unknown): value is JsonValue {
  if (value === null || ["string", "number", "boolean"].includes(typeof value)) return true;
  if (Array.isArray(value)) return value.every(isJsonValue);
  return isJsonObject(value) && Object.values(value).every(isJsonValue);
}
